import * as React from 'react';
import { useNavigate } from 'react-router-dom';
import { getEdgeSettingsPaths } from '@app/utils/appContext';
import {
  Breadcrumb,
  BreadcrumbItem,
  Button,
  Checkbox,
  Form,
  FormGroup,
  FormHelperText,
  Icon,
  Modal,
  ModalBody,
  ModalFooter,
  ModalHeader,
  ModalVariant,
  PageSection,
  Radio,
  Stack,
  StackItem,
  TextArea,
  TextInput,
  Title,
} from '@patternfly/react-core';
import { ExclamationTriangleIcon } from '@patternfly/react-icons';

const CreateRepository: React.FunctionComponent = () => {
  const navigate = useNavigate();

  const [repositoryName, setRepositoryName] = React.useState('');
  const [registryHostname, setRegistryHostname] = React.useState('');
  const [repositoryType, setRepositoryType] = React.useState<'git' | 'http' | 'oci'>('oci');
  const [scheme, setScheme] = React.useState<'https' | 'http'>('https');
  const [accessMode, setAccessMode] = React.useState<'readonly' | 'readwrite'>('readonly');
  const [useBasicAuth, setUseBasicAuth] = React.useState(false);
  const [username, setUsername] = React.useState('');
  const [password, setPassword] = React.useState('');
  const [skipServerVerification, setSkipServerVerification] = React.useState(false);
  const [caCertificate, setCaCertificate] = React.useState('');
  const [showCancelModal, setShowCancelModal] = React.useState(false);

  const hasFormData = () => {
    return repositoryName.trim() !== '' || 
           registryHostname.trim() !== '' || 
           username.trim() !== '' || 
           password.trim() !== '' || 
           caCertificate.trim() !== '';
  };

  const handleCancel = () => {
    if (hasFormData()) {
      setShowCancelModal(true);
    } else {
      navigateAway();
    }
  };

  const navigateAway = () => {
    navigate(getEdgeSettingsPaths(window.location.pathname).repositories);
  };

  const handleConfirmCancel = () => {
    navigateAway();
    setShowCancelModal(false);
  };

  const handleKeepCreating = () => {
    setShowCancelModal(false);
  };

  const handleSave = () => {
    // TODO: Implement save logic
    navigateAway(); // Navigate back after save
  };

  const isFormValid = repositoryName.trim() !== '' && registryHostname.trim() !== '';

  return (
    <>
      <PageSection hasBodyWrapper={false} style={{ paddingBottom: 0 }}>
        <div style={{ margin: 'var(--pf-t--global--spacer--md) var(--acm-page-inline-x, var(--pf-t--global--spacer--md))', paddingLeft: 'var(--acm-page-inline-x, var(--pf-t--global--spacer--md))', paddingRight: 'var(--acm-page-inline-x, var(--pf-t--global--spacer--md))' }}>
          <Breadcrumb style={{ marginBottom: 'var(--pf-t--global--spacer--md)' }}>
            <BreadcrumbItem to="#" onClick={(e) => { e.preventDefault(); handleCancel(); }}>
              Repositories
            </BreadcrumbItem>
            <BreadcrumbItem isActive>Create repository</BreadcrumbItem>
          </Breadcrumb>
          <Title headingLevel="h1" size="xl">
            Create repository
          </Title>
        </div>
      </PageSection>

      <PageSection hasBodyWrapper={false}>
        <div style={{ paddingLeft: '24px', paddingRight: '24px' }}>
          <Form style={{ maxWidth: '800px' }}>
            <Stack hasGutter>
              <StackItem>
                <FormGroup
                  label="Repository name"
                  isRequired
                  fieldId="repository-name"
                >
                  <div style={{ width: '400px' }}>
                    <TextInput
                      id="repository-name"
                      value={repositoryName}
                      onChange={(_, value) => setRepositoryName(value)}
                      aria-describedby="repository-name-helper"
                      style={{ width: '100%' }}
                    />
                  </div>
                  <FormHelperText id="repository-name-helper" hidden>
                    Enter a unique name for this repository
                  </FormHelperText>
                </FormGroup>
              </StackItem>

              <StackItem>
                <FormGroup
                  label="Registry hostname"
                  isRequired
                  fieldId="registry-hostname"
                >
                  <div style={{ width: '400px' }}>
                    <TextInput
                      id="registry-hostname"
                      value={registryHostname}
                      onChange={(_, value) => setRegistryHostname(value)}
                      aria-describedby="registry-hostname-helper"
                      style={{ width: '100%' }}
                    />
                  </div>
                  <FormHelperText id="registry-hostname-helper">
                    For example: quay.io, registry.redhat.io, myregistry.com:5000
                  </FormHelperText>
                </FormGroup>
              </StackItem>

              <StackItem>
                <FormGroup
                  label="Repository type"
                  fieldId="repository-type"
                  isRequired
                >
                  <Stack hasGutter>
                    <StackItem>
                      <Radio
                        id="repo-type-git"
                        name="repository-type"
                        label="Git repository"
                        isChecked={repositoryType === 'git'}
                        onChange={() => setRepositoryType('git')}
                      />
                    </StackItem>
                    <StackItem>
                      <Radio
                        id="repo-type-http"
                        name="repository-type"
                        label="HTTP service"
                        isChecked={repositoryType === 'http'}
                        onChange={() => setRepositoryType('http')}
                      />
                    </StackItem>
                    <StackItem>
                      <Radio
                        id="repo-type-oci"
                        name="repository-type"
                        label="OCI registry"
                        isChecked={repositoryType === 'oci'}
                        onChange={() => setRepositoryType('oci')}
                      />
                    </StackItem>
                  </Stack>
                </FormGroup>
              </StackItem>

              <StackItem>
                <FormGroup
                  label="Scheme"
                  fieldId="scheme"
                >
                  <Stack hasGutter>
                    <StackItem>
                      <Radio
                        id="scheme-https"
                        name="scheme"
                        label="HTTPS"
                        isChecked={scheme === 'https'}
                        onChange={() => setScheme('https')}
                      />
                    </StackItem>
                    <StackItem>
                      <Radio
                        id="scheme-http"
                        name="scheme"
                        label="HTTP"
                        isChecked={scheme === 'http'}
                        onChange={() => setScheme('http')}
                      />
                    </StackItem>
                  </Stack>
                </FormGroup>
              </StackItem>

              <StackItem>
                <FormGroup
                  label="Access mode"
                  fieldId="access-mode"
                >
                  <Stack hasGutter>
                    <StackItem>
                      <Radio
                        id="access-readonly"
                        name="access-mode"
                        label="View"
                        isChecked={accessMode === 'readonly'}
                        onChange={() => setAccessMode('readonly')}
                      />
                    </StackItem>
                    <StackItem>
                      <Radio
                        id="access-readwrite"
                        name="access-mode"
                        label="Manage"
                        isChecked={accessMode === 'readwrite'}
                        onChange={() => setAccessMode('readwrite')}
                      />
                    </StackItem>
                  </Stack>
                </FormGroup>
              </StackItem>

              <StackItem style={{ marginBottom: '0px' }}>
                <FormGroup
                  label="Advanced configurations"
                  fieldId="advanced-configurations"
                >
                </FormGroup>
              </StackItem>

              <StackItem style={{ marginTop: '0px', marginBottom: '0px' }}>
                <Checkbox
                  id="use-basic-auth"
                  label="Basic authentication"
                  isChecked={useBasicAuth}
                  onChange={(_, checked) => setUseBasicAuth(checked)}
                />
              </StackItem>

              {useBasicAuth && (
                <>
                  <StackItem style={{ paddingLeft: 'var(--pf-t--global--spacer--lg)' }}>
                    <FormGroup
                      label="Username"
                      isRequired
                      fieldId="username"
                    >
                      <div style={{ width: '400px' }}>
                        <TextInput
                          id="username"
                          value={username}
                          onChange={(_, value) => setUsername(value)}
                          style={{ width: '100%' }}
                        />
                      </div>
                    </FormGroup>
                  </StackItem>

                  <StackItem style={{ paddingLeft: 'var(--pf-t--global--spacer--lg)' }}>
                    <FormGroup
                      label="Password"
                      isRequired
                      fieldId="password"
                    >
                      <div style={{ width: '400px' }}>
                        <TextInput
                          id="password"
                          type="password"
                          value={password}
                          onChange={(_, value) => setPassword(value)}
                          style={{ width: '100%' }}
                        />
                      </div>
                    </FormGroup>
                  </StackItem>
                </>
              )}

              <StackItem>
                <Checkbox
                  id="skip-server-verification"
                  label="Skip server verification"
                  isChecked={skipServerVerification}
                  onChange={(_, checked) => setSkipServerVerification(checked)}
                />
              </StackItem>

              <StackItem style={{ paddingLeft: 'var(--pf-t--global--spacer--lg)' }}>
                <FormGroup
                  label="CA certificate"
                  fieldId="ca-certificate"
                >
                  <TextArea
                    id="ca-certificate"
                    value={caCertificate}
                    onChange={(_, value) => setCaCertificate(value)}
                    rows={6}
                    resizeOrientation="vertical"
                  />
                </FormGroup>
              </StackItem>

              <StackItem>
                <div style={{ display: 'flex', gap: 'var(--pf-t--global--spacer--sm)', alignItems: 'center' }}>
                  <Button
                    variant="primary"
                    onClick={handleSave}
                    isDisabled={!isFormValid}
                  >
                    Save
                  </Button>
                  <Button
                    variant="link"
                    onClick={handleCancel}
                    style={{ marginLeft: 'var(--pf-t--global--spacer--md)' }}
                  >
                    Cancel
                  </Button>
                </div>
              </StackItem>
            </Stack>
          </Form>
        </div>
      </PageSection>

      {/* Warning Modal - Data Loss Prevention */}
      <Modal
        variant={ModalVariant.small}
        isOpen={showCancelModal}
        onClose={handleKeepCreating}
      >
        <ModalHeader
          title="Discard repository creation?"
          titleIconVariant="warning"
        />
        <ModalBody>
          If you cancel creating this repository, the information you entered will not be saved.
        </ModalBody>
        <ModalFooter>
          <Button key="discard" variant="primary" onClick={handleConfirmCancel}>
            Discard and cancel
          </Button>
          <Button key="keep" variant="secondary" onClick={handleKeepCreating}>
            Keep creating
          </Button>
        </ModalFooter>
      </Modal>
    </>
  );
};

export { CreateRepository };

